import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";
import { useReducedMotion } from "react-native-reanimated";
import { View } from "tamagui";

import { Paragraph, Text } from "../Typography";
import BlinkingCursor from "./BlinkingCursor";

const TIME_TO_TYPE = 1000;
const DELAY_AFTER_TYPED = 200;
const ANIMATION_INTERVAL = TIME_TO_TYPE + DELAY_AFTER_TYPED;

export type TyperControllerProps = {
  content: string[];
  onTypingComplete?: () => void;
  boldStartIdx?: number;
  boldEndIdx?: number;
};

const Typer = ({
  content,
  contentQueryIdx,
  setContentQueryIdx,
  onTypingComplete,
  boldStartIdx,
  boldEndIdx,
}: TyperControllerProps & {
  contentQueryIdx: number;
  setContentQueryIdx: Dispatch<SetStateAction<number>>;
}) => {
  const [text, setText] = useState("");
  const reducedMotion = useReducedMotion();
  const typedHeightRef = useRef<number>(0);

  const appendTarget = content.slice(0, content.length + 1).join(" ");

  useEffect(() => {
    if (!content?.length || reducedMotion) return;

    const interval = setInterval(() => {
      setContentQueryIdx((prev) => (prev + 1) % content.length);
    }, ANIMATION_INTERVAL);
    return () => clearInterval(interval);
  }, [content, reducedMotion, setContentQueryIdx]);

  useEffect(() => {
    if (!content?.length) return;

    if (reducedMotion) {
      setText(appendTarget);
      onTypingComplete?.();
      return;
    }

    let interval: ReturnType<typeof setInterval>;

    interval = setInterval(() => {
      setText((typed) => {
        // Type out the next suggestion in full
        if (typed.length < appendTarget.length) {
          return appendTarget.slice(0, typed.length + 1);
        } else {
          // After a pause, move on to the next suggestion
          if (contentQueryIdx < content.length - 1) {
            setTimeout(() => {
              setContentQueryIdx((prev) =>
                Math.min(prev + 1, content.length - 1),
              );
            }, DELAY_AFTER_TYPED);
          } else {
            // All content has been typed, call completion callback
            setTimeout(() => {
              onTypingComplete?.();
            }, DELAY_AFTER_TYPED);
          }
          return typed;
        }
      });
    }, TIME_TO_TYPE / Math.max(appendTarget.length, 1));
    return () => clearInterval(interval);
  }, [
    appendTarget,
    reducedMotion,
    contentQueryIdx,
    setContentQueryIdx,
    onTypingComplete,
    content,
  ]);

  if (!typedHeightRef.current) {
    return (
      <View
        opacity={0}
        onLayout={(e) => {
          typedHeightRef.current = e.nativeEvent.layout.height;
        }}
      >
        <Paragraph
          {...(Platform.OS === "web" ? { translate: "no" } : {})}
          fontFamily="$serif"
        >
          {content.join(" ")}
        </Paragraph>
      </View>
    );
  }

  let formattedText: React.ReactNode[] = [text];
  if (boldStartIdx) {
    formattedText = [text.slice(0, boldStartIdx)];
    if (text.length > boldStartIdx) {
      formattedText.push(
        <Text fontFamily="$serif" bold>
          {text.slice(boldStartIdx, boldEndIdx)}
        </Text>,
      );
    }
    if (boldEndIdx && text.length > boldEndIdx) {
      formattedText.push(text.slice(boldEndIdx));
    }
  }

  return (
    <Paragraph
      {...(Platform.OS === "web" ? { translate: "no" } : {})}
      minHeight={typedHeightRef.current}
      fontFamily="$serif"
    >
      {formattedText.map((node, idx) => (
        <Fragment key={idx}>{node}</Fragment>
      ))}
      {/* Don't show blinking cursor when typing is complete */}
      {text.length < appendTarget.length && <BlinkingCursor compact />}
    </Paragraph>
  );
};

// We wrap Typer with this controller to easily reset its state via key.
const TyperController = (props: TyperControllerProps) => {
  const [contentQueryIdx, setContentQueryIdx] = useState<number>(0);
  return (
    <Typer
      key="append-only"
      contentQueryIdx={contentQueryIdx}
      setContentQueryIdx={setContentQueryIdx}
      {...props}
    />
  );
};

export default TyperController;
